import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePortfolio } from "@/context/PortfolioContext";
import { TXN_TYPES } from "@/lib/constants";
import { fmtCurrency } from "@/lib/format";
import { EMPTY_TXN, txnFlags, buildInitialForm, computeNet, validateTxn, buildTxnPayload } from "@/lib/transactionForm";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

function CashFields({ form, set, max }) {
    return (
        <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
                <Label>Amount ($)</Label>
                <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                    placeholder="0.00" data-testid="txn-amount-input" className="num" />
            </div>
            <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" max={max} value={form.transaction_date} onChange={(e) => set("transaction_date", e.target.value)}
                    data-testid="txn-date-input" className="num" />
            </div>
        </div>
    );
}

function TradeFields({ form, set, isDiv, feesEnabled, max }) {
    return (
        <>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label>Quantity</Label>
                    <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)}
                        placeholder="0" data-testid="txn-quantity-input" className="num" />
                </div>
                <div className="space-y-1.5">
                    <Label>{isDiv ? "$ / share" : "Price ($)"}</Label>
                    <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)}
                        placeholder="0.00" data-testid="txn-price-input" className="num" />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label className={!feesEnabled ? "text-muted-foreground/50" : ""}>Fees ($)</Label>
                    <Input type="number" value={form.fees} onChange={(e) => set("fees", e.target.value)} disabled={!feesEnabled}
                        placeholder={feesEnabled ? "0.00" : "—"} data-testid="txn-fees-input" className="num disabled:opacity-50" />
                </div>
                <div className="space-y-1.5">
                    <Label>Date</Label>
                    <Input type="date" max={max} value={form.transaction_date} onChange={(e) => set("transaction_date", e.target.value)}
                        data-testid="txn-date-input" className="num" />
                </div>
            </div>
            {isDiv && (
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label>Tax Withheld ($)</Label>
                        <Input type="number" value={form.tax_withheld} onChange={(e) => set("tax_withheld", e.target.value)}
                            placeholder="0.00" data-testid="txn-tax-input" className="num" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Net Amount ($)</Label>
                        <Input value={fmtCurrency(computeNet(form), { decimals: 2 })} readOnly disabled data-testid="txn-net-input" className="num disabled:opacity-70" />
                    </div>
                </div>
            )}
        </>
    );
}

function useTickerSubs(type, ticker, excludeId) {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!["SELL", "DIVIDEND"].includes(type) || !ticker) { setData(null); return undefined; }
        const h = setTimeout(async () => {
            try {
                const res = await api.get("/transactions/ticker-subs", { params: { ticker, exclude_id: excludeId || undefined } });
                setData(res.data);
            } catch { setData(null); }
        }, 350);
        return () => clearTimeout(h);
    }, [type, ticker, excludeId]);
    return data;
}

function HeldHint({ tsubs }) {
    if (!tsubs) return null;
    const held = tsubs.held || [];
    return (
        <p className="text-xs text-foreground/80 -mt-1" data-testid="txn-held-hint">
            {held.length
                ? <>Held in: {held.map((h) => `${h.sub} (${+h.shares.toFixed(4)} sh)`).join(", ")}{held.length > 1 ? " — pick the one you're selling from" : ""}</>
                : `${tsubs.ticker} isn't currently held in any sub-portfolio`}
        </p>
    );
}

export function TransactionDialog({ open, onOpenChange, editing, onSaved, prefill = null }) {
    const { subs } = usePortfolio();
    const [form, setForm] = useState(EMPTY_TXN);
    const [saving, setSaving] = useState(false);
    const todayISO = new Date().toISOString().slice(0, 10);

    useEffect(() => { setForm(buildInitialForm(editing || prefill, subs)); }, [editing, prefill, open, subs]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const type = form.transaction_type;
    const { isCash, isDiv, subEnabled, tickerEnabled, feesEnabled } = txnFlags(type);
    const tsubs = useTickerSubs(type, form.ticker, editing?.id);
    const held = tsubs?.held || [];
    const subOptions = held.length >= 2
        ? [...held.map((h) => ({ id: h.sub, name: h.sub })),
            ...(form.sub_portfolio && !held.some((h) => h.sub === form.sub_portfolio) ? [{ id: form.sub_portfolio, name: form.sub_portfolio }] : [])]
        : subs;

    useEffect(() => {
        if (!tsubs) return;
        const keep = editing && editing.ticker === tsubs.ticker && editing.transaction_type === type && editing.sub_portfolio;
        if (keep) return;
        const h = tsubs.held || [];
        if (h.length === 1) set("sub_portfolio", h[0].sub);
        else if (h.length >= 2) setForm((f) => (h.some((x) => x.sub === f.sub_portfolio) ? f : { ...f, sub_portfolio: "" }));
        else if (tsubs.bought?.length) set("sub_portfolio", tsubs.bought[0]);
    }, [tsubs]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async () => {
        const err = validateTxn(form);
        if (err) return toast.error(err);
        setSaving(true);
        try {
            const payload = buildTxnPayload(form);
            if (editing) await api.put(`/transactions/${editing.id}`, payload);
            else await api.post("/transactions", payload);
            toast.success(editing ? "Transaction updated" : "Transaction added");
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(apiErr(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="transaction-dialog">
                <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Transaction" : "Add Transaction"}</DialogTitle></DialogHeader>
                <div className="grid gap-3 py-1">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Type</Label>
                            <Select value={type} onValueChange={(v) => set("transaction_type", v)}>
                                <SelectTrigger data-testid="txn-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{TXN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className={!subEnabled ? "text-muted-foreground/50" : ""}>Sub-portfolio</Label>
                            <Select value={form.sub_portfolio} onValueChange={(v) => set("sub_portfolio", v)} disabled={!subEnabled}>
                                <SelectTrigger data-testid="txn-sub-select" className={!subEnabled ? "opacity-50" : ""}><SelectValue placeholder={subEnabled ? "Select" : "—"} /></SelectTrigger>
                                <SelectContent>{subOptions.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label className={!tickerEnabled ? "text-muted-foreground/50" : ""}>Ticker</Label>
                        <Input value={form.ticker} onChange={(e) => set("ticker", e.target.value.toUpperCase())} disabled={!tickerEnabled}
                            placeholder={tickerEnabled ? "e.g. AAPL" : "—"} data-testid="txn-ticker-input" className="num uppercase disabled:opacity-50" />
                    </div>
                    {["SELL", "DIVIDEND"].includes(type) && <HeldHint tsubs={tsubs} />}

                    {isCash ? <CashFields form={form} set={set} max={todayISO} /> : <TradeFields form={form} set={set} isDiv={isDiv} feesEnabled={feesEnabled} max={todayISO} />}

                    <div className="space-y-1.5">
                        <Label>Notes (optional)</Label>
                        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="txn-notes-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="txn-save-btn">
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {editing ? "Save changes" : "Add transaction"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
